import { h, Component, Prop, Element, State, Host } from '@stencil/core';
import { BrightcovePlayerService, BRIGHTCOVE } from '../../utils/BrightcoveService.js';

type VideoTypeProp = 'data-video-id' | 'data-playlist-id';

declare global {
  interface Window {
    PS: any;
  }
}
const PS = window.PS;

@Component({
  shadow: false,
  styleUrl: 'igws-video.less',
  tag: 'igws-video',
})
export class VideoComponent {
  @Element() el!: HTMLIgwsVideoElement;

  /** The ID of the video. Can be overwritten by `videoReference` property. */
  @Prop() videoId: string = '';

  /** The reference of the video. Can overwrite the `videoId`. */
  @Prop() videoReference: string = '';

  /** Comes from AEM `Player` option. Is attached to request url. Default: `2834081556001` (single player). */
  @Prop() playerType: string = BRIGHTCOVE.AEM.SINGLE_PLAYER_ID;

  /** Account ID attached to api request. Default: `2830693144001`. */
  @Prop() accountId: string = BRIGHTCOVE.ACCOUNT.ID;

  /** Will initialize player when `true`. Default: `false`. */
  @Prop() isLive: boolean = false;

  /** Will initialize and play video when `true`. Default: `false`. */
  @Prop() autoStart: boolean = false;

  /** Overlay property. Will display overlay with `replay button` when video ends.  Default: `false` */
  @Prop() showoverlay: boolean = false;

  /** Overlay content. Will display with additional text in overlay. */
  @Prop() overlayText: string = '';

  /** TODO */
  @Prop() beforeLiveText: string = '';

  /** Overlay content. Will display additional link button. */
  @Prop() ctaText: string = '';

  /** Link for `ctaText` link button. */
  @Prop() ctaUrl: string = '';

  /** Overlay content. Will display additional secondary link button. */
  @Prop() ctaTextSecondary: string = '';

  /** Link for `ctaTextSecondary` link button. */
  @Prop() ctaUrlSecondary: string = '';

  /** Shows the description if it exists in the video mediainfo. https://player.support.brightcove.com/references/video-metadata-mediainfo.html */
  @Prop() showDescription: boolean = false;

  @State() isOverlayVisible: boolean = false;
  @State() isPosterImageVisible: boolean = true;
  @State() videoMediaInfo: any;

  private isVideoPlayerLoaded: boolean = false;
  private hasTranscriptSlot: boolean = !!this.el.querySelector('[slot="transcript"]');
  private hasImgSlot: boolean = !!this.el.querySelector('[slot="img"]');
  private playerTypeToDownload: Map<any, any> = new Map();
  private playerService: BrightcovePlayerService = new BrightcovePlayerService();
  private bctid = new URLSearchParams(window.location.search).get('bctid');
  private bclid = new URLSearchParams(window.location.search).get('bclid');

  get shouldRenderPlaylist() {
    return [BRIGHTCOVE.AEM.PLAYLIST_BELOW_ID, BRIGHTCOVE.AEM.PLAYLIST_RIGHT_ID].includes(this.playerType);
  }

  get shouldGetVideoIdFromUrl() {
    return this.bctid && this.bclid && this.playerType !== BRIGHTCOVE.AEM.SINGLE_PLAYER_ID;
  }

  get computedPlayerId(): string {
    return this.playerTypeToDownload.get(this.playerType);
  }

  get computedVideoTypeProperty(): VideoTypeProp {
    return this.playerType === BRIGHTCOVE.AEM.SINGLE_PLAYER_ID ? 'data-video-id' : 'data-playlist-id';
  }

  get computedVideoId(): string {
    if (this.shouldGetVideoIdFromUrl) {
      return this.bclid || '';
    }
    return this.videoReference ? `ref:${this.videoReference}` : this.videoId;
  }

  get computedHookId(): string {
    if (this.shouldGetVideoIdFromUrl) {
      return `id-${this.bctid}-video`;
    }
    return this.videoReference ? `id-${this.videoReference}-video` : `id-${this.videoId}-video`;
  }

  get brightcovePlayerUrl(): string {
    return `https://players.brightcove.net/${this.accountId}/${this.computedPlayerId}_default/index.min.js`;
  }

  disconnectedCallback() {
    document.head.querySelector(`script[src="${this.brightcovePlayerUrl}"]`)?.remove();
  }

  async componentWillLoad() {
    // Match id from AEM with player id from brightcove
    this.playerTypeToDownload.set(BRIGHTCOVE.AEM.SINGLE_PLAYER_ID, BRIGHTCOVE.SCRIPT.DEFAULT_ID);
    this.playerTypeToDownload.set(BRIGHTCOVE.AEM.PLAYLIST_BELOW_ID, BRIGHTCOVE.SCRIPT.PLAYLIST_BELOW_ID);
    this.playerTypeToDownload.set(BRIGHTCOVE.AEM.PLAYLIST_RIGHT_ID, BRIGHTCOVE.SCRIPT.PLAYLIST_RIGHT_ID);
    this.playerTypeToDownload.set(BRIGHTCOVE.AEM.LIVE_PLAYER_ID, BRIGHTCOVE.SCRIPT.DEFAULT_ID);
  }

  async componentDidLoad() {
    if (this.hasTranscriptSlot) this.handleTranscriptToggle();
    if (this.autoStart) return this.loadPlayerAndPlay();
    if (this.playerType !== BRIGHTCOVE.AEM.SINGLE_PLAYER_ID || this.isLive || !this.hasImgSlot) await this.loadPlayer();
  }

  private replay(e: UIEvent): void {
    e.preventDefault();
    this.isOverlayVisible = false;
    this.playerService.replayVideo();
  }

  private makeOverlayActive(): void {
    if (this.showoverlay) {
      this.isOverlayVisible = true;
    }
  }

  private handleTranscriptToggle() {
    const link = this.el.querySelector('.igws-video-transcript a');
    if (!link) return;
    const content = link.nextElementSibling;
    link.addEventListener('click', e => {
      e.preventDefault();
      if (content) content.classList.toggle('show');
    });
  }

  private handlePosterClickEvent() {
    this.isPosterImageVisible = false;
    this.loadPlayerAndPlay();
  }

  private registerGaTrackingPlayerEvents() {
    if (PS?.GAtracking?.TrackWebinarPlayer) {
      this.playerService.onVideo('play', () => {
        PS.GAtracking.TrackWebinarPlayer.getVideoData(this.playerService.player, 'play');
      });
      this.playerService.onVideo('pause', () => {
        PS.GAtracking.TrackWebinarPlayer.getVideoData(this.playerService.player, 'pause');
      });
      this.playerService.onVideo('timeupdate', () => {
        PS.GAtracking.TrackWebinarPlayer.getVideoData(this.playerService.player, 'percentile');
      });
      this.playerService.onVideo('ended', () => {
        PS.GAtracking.TrackWebinarPlayer.getVideoData(this.playerService.player, 'video complete');
      });
    }
  }

  private registerPlayerEvents() {
    this.playerService.onVideo('ended', () => this.makeOverlayActive());
    this.playerService.onVideo('play', () => (this.isPosterImageVisible = false));
    this.playerService.onVideo('loadstart', (video: any) => (this.videoMediaInfo = video.target?.player?.mediainfo));
  }

  private async loadPlayer() {
    if (this.isVideoPlayerLoaded) return;

    await this.playerService.downloadBrightcoveScript(this.brightcovePlayerUrl);
    this.playerService.preparePlayer(this.computedHookId);

    this.registerPlayerEvents();
    this.registerGaTrackingPlayerEvents();

    this.isVideoPlayerLoaded = true;
  }

  private async loadPlayerAndPlay(isMuted?: boolean) {
    await this.loadPlayer();
    this.playerService.playVideo(isMuted);
  }

  private brighcovePlayerTemplate(): any {
    const props = {
      id: this.computedHookId,
      [this.computedVideoTypeProperty]: this.computedVideoId,
      'data-account': this.accountId,
      'data-player': this.computedPlayerId,
      'data-embed': 'default',
      class: 'video-js',
      preload: 'auto',
      controls: true,
    };

    return <video {...props}></video>;
  }

  private playlistTemplate() {
    return (
      <div class={`igws-video__playlist-wrapper show`}>
        <div data-for={this.computedHookId} class="vjs-playlist vjs-playlist-horizontal"></div>
      </div>
    );
  }

  private overlayTemplate() {
    const beforeLiveText = (
      <p class="igws-video__overlay-text igws-video__overlay-text--beforelive">{this.beforeLiveText}</p>
    );
    const overlayText = <p class="igws-video__overlay-text">{this.overlayText}</p>;
    const ctaText = (
      <a class="igws-video__cta-button igws-video__cta-button--primary" href={this.ctaUrl}>
        {this.ctaText}
      </a>
    );
    const ctaTextSecondary = (
      <a class="igws-video__cta-button igws-video__cta-button--secondary" href={this.ctaUrlSecondary}>
        {this.ctaTextSecondary}
      </a>
    );

    const replayButton = (
      <a onClick={e => this.replay(e)} class="igws-video__replay-video" href="#">
        Replay Video
      </a>
    );

    return (
      <div class={`igws-video__overlay ${this.isOverlayVisible ? 'active' : ''}`}>
        {this.beforeLiveText && this.isLive ? beforeLiveText : null}
        {this.overlayText && !this.isLive ? overlayText : null}
        {this.ctaText || this.ctaTextSecondary ? (
          <div>
            {this.ctaText ? ctaText : null}
            {this.ctaTextSecondary ? ctaTextSecondary : null}
          </div>
        ) : null}
        {!this.isLive ? replayButton : null}
      </div>
    );
  }

  private descriptionTemplate() {
    return (
      <div class="igws-video-description">
        <p class="igws-video-description_content">{this.videoMediaInfo?.description}</p>
      </div>
    );
  }

  render() {
    return (
      <Host>
        <div class="igws-video">
          <div class="igws-video__brightcove-player">
            {this.brighcovePlayerTemplate()}
            {this.shouldRenderPlaylist ? this.playlistTemplate() : null}
          </div>
          {this.showoverlay ? this.overlayTemplate() : null}
          <div
            class={`igws-video__poster-img-slot ${this.isPosterImageVisible ? '' : 'hide'}`}
            onClick={() => this.handlePosterClickEvent()}
          >
            <slot name="img"></slot>
          </div>
        </div>
        {this.showDescription ? this.descriptionTemplate() : null}
        <div class="igws-video-transcript">
          <slot name="transcript"></slot>
        </div>
      </Host>
    );
  }
}
